import React from 'react';
import { useTranslation } from 'react-i18next';
import { JourneyOverlay, Stepper } from '@hvcgroep/design-system';
import { RegistrationDeskEventState, RegistrationDeskEventType } from '../../state-machine';
import { RegistrationDeskStepComponent, RegistrationDeskStepProps } from '../../types/multistep-form';
import { APP_ID } from '../../../../shared/config/constants';
import gtm from '../../../../shared/helpers/gtm';

export type Props<E extends RegistrationDeskEventType, P = {}> = {
  /*
   * The current value of the "form" state. Used to calculate stepper progress.
   * If the form is not set, the stepper will not be displayed.
   */
  readonly currentStep?: RegistrationDeskEventState;
  readonly onCloseButtonClick: () => void;
  readonly component: RegistrationDeskStepComponent<E, P>;
  readonly componentProps: RegistrationDeskStepProps<E, P>;
  readonly hideJourneyOverlay?: boolean;
};

const RegistrationDeskStep = <E extends RegistrationDeskEventType, P = {}>(props: Props<E, P>) => {
  const {
    currentStep,
    onCloseButtonClick,
    component: Component,
    componentProps,
    hideJourneyOverlay,
  } = props;

  const { t } = useTranslation();

  const steps: {
    key: RegistrationDeskEventState['form'];
    label: string
  }[] = [
    {
      key: 'enteringUsage',
      label: t('registration_desk.screens.adjust_usage.stepper_title'),
    },
    {
      key: 'enteringPersonalDetails',
      label: t('registration_desk.screens.personal_details.stepper_title'),
    },
    {
      key: 'enteringDeliveryDetails',
      label: t('registration_desk.screens.delivery_details_form.stepper_title'),
    },
    {
      key: 'enteringPaymentDetails',
      label: t('registration_desk.screens.payments_details_form.stepper_title'),
    },
    {
      key: 'reviewingTermsAndConditions',
      label: t('registration_desk.screens.confirm.stepper_title'),
    },
  ];

  if (hideJourneyOverlay) {
    return (
      <Component
        {...componentProps}
        onProceed={(e) => {
          componentProps.onProceed(e);
          gtm.trackUserEvent(String(APP_ID), `From: ${currentStep?.form}`, t('waste_calendar.navigation.next'));
        }}
        onGoBack={(e) => {
          if (componentProps.onGoBack) {
            componentProps.onGoBack(e);
          }
          gtm.trackUserEvent(String(APP_ID), `From: ${currentStep?.form}`, t('waste_calendar.navigation.prev'));
        }}
      />
    );
  }

  const currentlyActiveStep = steps.findIndex((step) => {
    return currentStep && step.key === currentStep.form;
  });

  return (
    <JourneyOverlay
      onCloseButtonClick={onCloseButtonClick}
    >
      {currentStep && (
        <Stepper
          active={currentlyActiveStep}
          steps={steps}
        />
      )}
      <Component
        {...componentProps}
        onProceed={(e) => {
          componentProps.onProceed(e);
          gtm.trackUserEvent(String(APP_ID), `From: ${currentStep?.form}`, t('waste_calendar.navigation.next'));
        }}
        onGoBack={(e) => {
          if (componentProps.onGoBack) {
            componentProps.onGoBack(e);
          }
          gtm.trackUserEvent(String(APP_ID), `From: ${currentStep?.form}`, t('waste_calendar.navigation.prev'));
        }}
      />
    </JourneyOverlay>
  );
};

export default RegistrationDeskStep;
